import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HouseholdProvider } from './contexts/HouseholdContext';
import { NotificationProvider } from './components/ui/NotificationSystem/NotificationContext';
import NotificationSystem from './components/ui/NotificationSystem';
import MainLayout from './components/layouts/MainLayout';
import LoginScreen from './features/LoginScreen';
import RegisterScreen from './features/RegisterScreen';
import ActivateAccount from './features/ActivateAccount';
import AcceptHouseholdInvitation from './features/AcceptHouseholdInvitation';
import ForgotPassword from './features/ForgotPassword';
import ResetPassword from './features/ResetPassword';
import ResendActivationEmail from './features/ResendActivationEmail';
import ShoppingLists from './features/ShoppingLists';
import ShoppingListView from './features/ShoppingListView';
import Products from './features/Products';
import Households from './features/Households';
import { AccountManagement } from './features/AccountManagement';

const theme = createTheme({
  palette: {
    primary: {
      main: '#451b0c',
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isInitializing } = useAuth();

  if (isInitializing) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <LoginScreen />;
  }

  return <MainLayout>{children}</MainLayout>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <AuthProvider>
          <Router>
            <HouseholdProvider>
              <Routes>
                <Route path="/register" element={<RegisterScreen />} />
                <Route path="/activate-account/:id" element={<ActivateAccount />} />
                <Route path="/accept-household-invitation/:id" element={<AcceptHouseholdInvitation />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:requestId" element={<ResetPassword />} />
                <Route path="/resend-activation-email" element={<ResendActivationEmail />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <ShoppingLists />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopping-lists"
                  element={
                    <ProtectedRoute>
                      <ShoppingLists />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopping-lists/:shoppingListId"
                  element={
                    <ProtectedRoute>
                      <ShoppingListView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <ProtectedRoute>
                      <Products />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/households"
                  element={
                    <ProtectedRoute>
                      <Households />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <AccountManagement />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <NotificationSystem />
            </HouseholdProvider>
          </Router>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
